import { Button } from '@givebutter/primitives/button';
import { useLoadingDelay } from '@givebutter/react-util';
import { faBarsFilter, faSearch, faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SearchCollectionName } from 'api/search';
import * as DropdownMenu from 'components/dropdown-menu';
import { SEARCH_INPUT, SEARCH_RESULTS } from 'components/search/selectors';
import { collectionNameToReadable } from 'components/search/transform';
import { useSearchStore } from 'components/search/useSearchStore';
import { IntercomProductTourTargets } from 'constants/intercom';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { twJoin } from 'tailwind-merge';

const SearchBar = () => {
    const { isOpen, query, setQuery, searchRef, listRef } = useSearchStore();

    return (
        <div className={'flex items-center gap-4 px-4 pt-2'}>
            <div className={'flex flex-grow items-center gap-1.5'}>
                <Icon />
                <input
                    id={SEARCH_INPUT}
                    placeholder={'Search across Givebutter'}
                    ref={searchRef}
                    role={'combobox'}
                    autoComplete={'off'}
                    autoCapitalize={'none'}
                    autoCorrect={'off'}
                    aria-controls={SEARCH_RESULTS}
                    aria-autocomplete={'both'}
                    aria-expanded={isOpen}
                    aria-label={'Global search'}
                    value={query}
                    onChange={e => {
                        const value = e.target.value;
                        setQuery(value);
                    }}
                    onKeyUp={e => {
                        if (e.key === 'ArrowDown') {
                            const firstFoundLi = listRef.current?.querySelector('[role="option"]');
                            (firstFoundLi as HTMLElement | null | undefined)?.focus();
                        }
                    }}
                    className={'flex-grow text-gray-900'}
                />
                <label htmlFor={SEARCH_INPUT} className={'sr-only'}>
                    Search
                </label>
            </div>
            <div className={'flex items-center gap-2'}>
                <ClearButton />
                <FilterDropdown />
            </div>
        </div>
    );
};

const Icon = () => {
    const { query, isTypesensePending } = useSearchStore();
    const isNotEmpty = query.length > 0;
    const { isLoading } = useLoadingDelay(isTypesensePending);
    const isSearching = isLoading && isNotEmpty;

    return (
        <FontAwesomeIcon
            className={twJoin(isSearching && 'animate-spin', 'text-gray-500')}
            icon={isSearching ? faSpinnerThird : faSearch}
        />
    );
};

const ClearButton = () => {
    const { query, clear, searchRef } = useSearchStore();

    if (query.length === 0) {
        return null;
    }

    return (
        <Button
            variant="none"
            size="sm"
            aria-label={'Clear search contents'}
            onClick={() => {
                clear();
                searchRef?.current?.focus();
            }}
            className={'font-medium text-gray-400'}
        >
            Clear
        </Button>
    );
};

const FilterDropdown = () => {
    const { resourceFilter, setResourceFilter } = useSearchStore();
    const { searchAllowedResources } = useCurrentAccount();

    return (
        <DropdownMenu.Root>
            <DropdownMenu.Trigger
                aria-label={'Search bar filter dropdown'}
                data-intercom-target={IntercomProductTourTargets.GlobalSearchFilter}
            >
                <FontAwesomeIcon className={'leading-none text-gray-700'} icon={faBarsFilter} />
            </DropdownMenu.Trigger>
            <DropdownMenu.Content align={'end'} sideOffset={4}>
                <DropdownMenu.CheckboxItem
                    checked={resourceFilter.length === 0}
                    onCheckedChange={checked =>
                        checked
                            ? setResourceFilter([])
                            : setResourceFilter(
                                  (
                                      [
                                          'contacts',
                                          'transactions',
                                          'campaigns',
                                          'transaction_plans',
                                          'tickets',
                                          'help_center',
                                      ] as SearchCollectionName[]
                                  ).filter(resourceFilter =>
                                      searchAllowedResources.includes(resourceFilter),
                                  ),
                              )
                    }
                >
                    All
                </DropdownMenu.CheckboxItem>
                <FilterDropdownCheckboxItem resource={'contacts'} />
                <FilterDropdownCheckboxItem resource={'transactions'} />
                <FilterDropdownCheckboxItem resource={'campaigns'} />
                <FilterDropdownCheckboxItem resource={'transaction_plans'} />
                <FilterDropdownCheckboxItem resource={'tickets'} />
                <FilterDropdownCheckboxItem resource={'help_center'} />
            </DropdownMenu.Content>
        </DropdownMenu.Root>
    );
};

interface FilterDropdownCheckboxItemProps
    extends Omit<DropdownMenu.DropdownMenuCheckboxItemProps, 'children' | 'checked'> {
    resource: SearchCollectionName;
}

const FilterDropdownCheckboxItem = (props: FilterDropdownCheckboxItemProps) => {
    const { className = '', resource, ...rest } = props;
    const { searchAllowedResources } = useCurrentAccount();
    const { resourceFilter, addResourceFilter, removeResourceFilter } = useSearchStore();
    const isChecked = resourceFilter.includes(resource);

    if (!searchAllowedResources.includes(resource)) {
        return null;
    }

    return (
        <DropdownMenu.CheckboxItem
            checked={isChecked}
            onCheckedChange={checked =>
                checked ? addResourceFilter(resource) : removeResourceFilter(resource)
            }
            {...rest}
        >
            {collectionNameToReadable(resource)}
        </DropdownMenu.CheckboxItem>
    );
};

export { SearchBar };
